import { type FC } from 'react';
import { Alert, Grid, Popconfirm } from 'antd';
import { Button, Space } from '@g2u/ui-kit';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router';
import { format, parse } from 'date-fns';
import cx from 'classnames';

import { Breadcrumb, PopconfirmDescription, Steps, CustomerTime, LegacyCustomerAlert } from 'components';
import { type WithOptionalDataTest } from 'types';
import { BookingStore, RootStore } from 'store';
import styles from './booking-progress.module.scss';

type Props = {
  step: number;
  sticky?: boolean;
} & WithOptionalDataTest;

export const BookingProgress: FC<Props> = observer((props) => {
  const { step, sticky } = props;
  const { isTrainingMode } = RootStore;
  const { resetBookingProgress, slot, customer, previousJob, techniciansList, setPreviousJob } = BookingStore;
  const { useBreakpoint } = Grid;
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const showPreviousTech = step === 2 && !!previousJob?.technician && !slot;
  const isPreviousTechSuitable = techniciansList?.some((technician) =>
    technician.value.split(',').map(Number).includes(previousJob?.technician?.ID),
  );
  const previosTechMessage = `Previous tech: ${previousJob?.technician?.name} - 
      ${previousJob?.job?.appointment && format(parse(previousJob?.job?.appointment, 'yyyy-MM-dd HH:mm:ss', new Date()), 'dd MMM yyyy')} 
      ${!isPreviousTechSuitable ? '- doesn’t have either the required skillset / service type / service area, or has been set to unavailable.' : ''}`;

  const handleRestartBooking = () => {
    resetBookingProgress();
    navigate('/booking/start');
  };

  const breadcrumbConfig = [
    {
      url: '/booking/start',
      action: () => null,
      title:
        step < 4 ? (
          <Popconfirm
            title="Alert"
            onConfirm={() => {
              handleRestartBooking();
            }}
            description={<PopconfirmDescription hasSlot={Boolean(slot)} />}
            okText="Yes"
          >
            Jobs
          </Popconfirm>
        ) : (
          <span onClick={handleRestartBooking}>Jobs</span>
        ),
    },
    {
      title: 'New Booking',
    },
  ];

  return (
    <div className={cx(styles.bookingProgress, sticky && styles.bookingProgress_sticky)}>
      <Space.$
        fullWidth
        className={styles.bookingProgress__content}
        size={Space.Size.Middle}
        direction={Space.Direction.Vertical}
      >
        <Breadcrumb dataTestId="breadcrumb" links={breadcrumbConfig} hasTime={false} />
        <Steps
          current={step}
          isTraining={isTrainingMode}
          labelPlacement={screens.lg ? 'horizontal' : 'vertical'}
          hasSlot={Boolean(slot)}
        />
      </Space.$>
      <CustomerTime isSticky />
      {customer && step < 4 && <LegacyCustomerAlert customer={customer} />}
      {showPreviousTech && (
        <Alert
          message={previosTechMessage}
          type={isPreviousTechSuitable ? 'info' : 'warning'}
          showIcon
          closable
          action={
            isPreviousTechSuitable ? (
              <Button.$
                size={Button.Size.Small}
                onClick={() => setPreviousJob({ ...previousJob, active: true })}
                dataTestId="legacy-profile-link"
              >
                Check Availability
              </Button.$>
            ) : null
          }
        />
      )}
      <div id="booking-progress-portal"></div>
    </div>
  );
});
